import { Color } from '@persona-id/design-tokens';
import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS } from 'shared/styles/variables';
import styled, { createGlobalStyle, css, DefaultTheme, keyframes } from 'styled-components';

import { TransitionDuration } from 'design-system/variables/transitions';
import ImageIdBack from 'images/inquiry/scanner/id-back.svg';
import ImageIdBarcode from 'images/inquiry/scanner/id-barcode.svg';
import ImageIdFront from 'images/inquiry/scanner/id-front.svg';
import ImagePassportPortrait from 'images/inquiry/scanner/passport-portrait.svg';
import ImagePassportSignature from 'images/inquiry/scanner/passport-signature.svg';
import IdBarcodeGenericGuide from 'images/pictographs/barcode-generic-guide.svg';
import { NAVBAR_ID } from 'inquiry/components/ConnectedNavbar/ConnectedNavbar';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import IdBackGuide from 'inquiry/components/ScannerOverlay/IdBackGuide';
import IdBarcodeGuide from 'inquiry/components/ScannerOverlay/IdBarcodeGuide';
import IdCornersOnlyGuide from 'inquiry/components/ScannerOverlay/IdCornersOnlyGuide';
import IdFrontGuide from 'inquiry/components/ScannerOverlay/IdFrontGuide';
import IdFrontRightPortraitGuide from 'inquiry/components/ScannerOverlay/IdFrontRightPortraitGuide';
import MrzTwoLineGuide from 'inquiry/components/ScannerOverlay/MrzTwoLineGuide';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import useIsMounted from 'lib/hooks/useIsMounted';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import {
  SerializedStepGovernmentIdStyles,
  StepGovernmentIdConfig,
} from 'lib/interfaces/StepsConfig';
import { TextComponentStyles } from 'lib/interfaces/ui-components';
import {
  ClientSideOverlay,
  GovernmentIdOverlay,
  GovernmentIdSide,
} from 'lib/interfaces/verificationConfig';
import { captureMessage } from 'lib/reporting';
import ThemedSvg from 'verify/components/ThemedSvg';

export const COLLAPSE_ANIMATION_DURATION_SECONDS = 1;
export const FLIP_ANIMATION_DURATION_SECONDS = 1;
const BORDER_RADIUS = 8;
const BORDER_WIDTH = 3;
const OVERLAY_HINT_VERTICAL_PADDING = 14;

const getBorderRadius = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => {
  return sizeCss(styles?.border?.radius) ?? `${BORDER_RADIUS}px`;
};

const getBorderWidth = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => sizeCss(styles?.border?.width?.top) ?? `${BORDER_WIDTH}px`;
const getBorderColor = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => colorCss(styles?.border?.color) ?? COLORS.white;
const getInnerBorderRadius = (
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) => `calc(${getBorderRadius(styles)} - ${getBorderWidth(styles)})`;
const getScanAnimationColor = (
  theme: DefaultTheme,
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'],
) =>
  colorCss((styles as SerializedStepGovernmentIdStyles['captureFeedBox'])?.stroke?.color) ??
  theme.iconColorPrimary;

// The Percent Width/Height the visible capture window is compared to the size of the real window
// Note that chnaging this will still likely require updates to video positioning in the Scanner component,
// as well as changes to how the image is extracted from the video in bufferImageWithOverlayWindow
export const VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO = 0.8;
export const VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO = 0.8;

export const SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO = 0.4247;

const CropPositioner = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const OverlayCrop = styled('div')`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

interface ScannerOverlayProps {
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  capturedImageUrl?: string;
  governmentIdClass: GovernmentIdClass;
  requiresSide: GovernmentIdSide;
  captureOverlay?: GovernmentIdOverlay;
  mirrored?: boolean;

  captureFrameLabel?: string;
  reverseCaptureFrameLabel?: string;
  hint?: string;
  prompt?: string;

  scanning?: boolean;
  capturing?: boolean;

  collapsed?: boolean;
  flipped?: boolean;
  reverseSide?: GovernmentIdSide;
  expandOnMount?: boolean;

  overlayRef?: RefObject<HTMLDivElement | undefined>;
  overlayCaptureWindowRef?: RefObject<HTMLDivElement | undefined>;
  previewViewfinderRef?: RefObject<HTMLDivElement | undefined>;
  onReady?: () => void;

  isCameraReady?: boolean;
  hasDetectedQuad?: boolean;
  styles?: {
    hint: TextComponentStyles;
    base: SerializedStepGovernmentIdStyles['base'];
    captureBox: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
    captureOverlayHeaderButton: SerializedStepGovernmentIdStyles['captureOverlayHeaderButton'];
    captureHintIcon: SerializedStepGovernmentIdStyles['captureHintIcon'];
  };

  environment?: string;
  isShortBarcodeOverlay?: boolean;

  renderUnderOverlayComponents?: () => ReactNode;
  videoReady: boolean;
}

const ScannerOverlay = (props: ScannerOverlayProps) => {
  const defaultOverlayRef = useRef<HTMLDivElement | undefined>();

  const navbarButtonColor = colorCss(props?.styles?.captureOverlayHeaderButton?.stroke?.color);

  return (
    <>
      <FullscreenOverlayGlobalStyles svgColor={navbarButtonColor} />
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <CropPositioner ref={props.overlayRef ?? defaultOverlayRef}>
        <OverlayCrop>
          <InnerOverlay {...props} />
        </OverlayCrop>
      </CropPositioner>
    </>
  );
};

/**
 * Ignore custom nav background color here -- always make it appear black by being transparent.
 * We DO allow custom icon colors, though, for parity with mobile SDKs.
 *
 * @see front-end/verify/components/Navbar/Navbar.tsx
 **/
const FullscreenOverlayGlobalStyles = createGlobalStyle<{ svgColor?: string | null }>`
  nav#${NAVBAR_ID} {
    background-color: unset;

    svg {
      path {
        ${({ svgColor }) => {
          // default override color of back button to always be white
          return `stroke: ${svgColor ?? COLORS.white};`;
        }}
      }
    }
  }
`;

const BarcodeImageContainer = styled('div')`
  position: absolute;
  margin-left: 20%;
  margin-right: 20%;
  padding-top: 17%;
  width: 60%;
  top: 50%;
  transform: translateY(-50%);
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

// wrapper component contains both the visual capture window and the larger real capture window
// we use the scrollHeight to compute how tall the imagecontainer needs to get during collapse animation,
// so we dont' want to put the real capture window inside of the image container as that will affect scrollHeight
const ImageContainerWrapper = styled.div`
  position: relative;

  // captured image should be visible in the gap left by the border radius of the hint
  overflow: visible;
  z-index: -1;
`;

const ImageContainer = styled('div')`
  position: relative;
  overflow: hidden;
  transition: all ${COLLAPSE_ANIMATION_DURATION_SECONDS}s;
`;

const Positioner = styled('div')<{ mirrored: boolean }>`
  ${({ mirrored }) =>
    mirrored &&
    css`
      transform: scale(-1, 1);
    `};
  transform: translate(-50%, -50%) ${({ mirrored }) => (mirrored ? 'scale(-1, 1)' : '')};
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;

  & > div,
  & > img {
    margin: auto;
  }
`;

const ImageContainerSizer = styled('div')<{ isShortBarcodeOverlay: boolean }>`
  // Aspect ratio different for MRZ
  top: 0;
  left: 0;
  padding-bottom: ${({ isShortBarcodeOverlay }) => (isShortBarcodeOverlay ? '35%' : '66%')};
  overflow: hidden;
`;

const IdBarcodeGenericGuideStyled = styled(IdBarcodeGenericGuide)`
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const SimpleGuide = styled('div')`
  padding-bottom: 65.97%;
  width: 100%;
`;

const OverlayPortrait = styled('div')<{ isReverseSide?: boolean }>`
  backface-visibility: hidden;
  height: 100%;
  width: 100%;
  overflow: hidden;
  // need to set a transform even if it isn't the reverse side for backface visibilty to work on Safari
  // probably has to do with forcing hardware acceleration?
  transform: ${({ isReverseSide }) => (isReverseSide ? 'rotateY(180deg)' : 'scale(1)')};
  position: ${({ isReverseSide }) => (isReverseSide ? 'absolute' : null)};
  top: ${({ isReverseSide }) => (isReverseSide ? 0 : null)};
`;

const FlipCard = styled('div')<{
  collapsed?: boolean;
  collapsedVerticalOffset?: string | null;
  isShortBarcodeOverlay?: boolean;
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
}>`
  position: absolute;
  top: calc(
    ${({ collapsed, isShortBarcodeOverlay }) =>
        collapsed ? '50%' : isShortBarcodeOverlay ? '35%' : '25%'} -
      ${({ styles }) => getBorderWidth(styles)}
  );
  background: ${({ collapsed }) => (collapsed ? 'black' : 'transparent')};
  width: calc(80% + 2 * (${({ styles }) => getBorderWidth(styles)}));
  margin-left: calc(10% - ${({ styles }) => getBorderWidth(styles)});
  margin-right: calc(10% - ${({ styles }) => getBorderWidth(styles)});
  perspective: 1000px;
  transition: all ${COLLAPSE_ANIMATION_DURATION_SECONDS}s;
`;

const FlipCardInner = styled('div')<{
  flipped?: boolean;
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
}>`
  border: ${({ styles }) => `${getBorderWidth(styles)} solid ${getBorderColor(styles)}`};
  border-radius: ${({ styles }) => getBorderRadius(styles)};
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  // overflow:hidden prevents content inside from cutting into the outer border
  // when flipping don't set overflow:hidden, because overflow:hidden breaks backface visbility for flip animation
  // this should be fine because flipping only occurs when we are in collapsed mode,
  // and the hint card has border radius set appropriately to not cut into the outer border
  overflow: ${({ flipped }) => (flipped ? null : 'hidden')};

  transition: transform ${FLIP_ANIMATION_DURATION_SECONDS}s;
  box-shadow: 0 0 1px 1000px rgba(0, 0, 0, 1);
`;

const AboveFrameHintContainer = styled('div')<{ visible: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -16px;
  transform: translateY(-100%);
  border-radius: 4px;
  padding: 8px 16px;
  background-color: rgba(255, 255, 255, 0.15);
  color: ${COLORS.white};
  width: 100%;
  text-align: center;
  font-size: 14px;
  line-height: 150%;
  z-index: 1;

  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

const UnderOverlayComponentsWrapper = styled('div')<{ visible: boolean }>`
  position: absolute;
  bottom: -16px;
  transform: translateY(100%);
  z-index: 1;

  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  width: 100%;
`;

const NewHintContainer = styled('div')<{ visible: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${COLORS.white};
  text-align: center;
  font-size: 14px;
  line-height: 150%;
  z-index: 1;
  width: 80%;

  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
`;

const NewHintContainerInner = styled('div')`
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  padding: 8px 16px;
  min-width: 50%;
`;

const CaptureFrameLabel = styled('div')<{
  innerBorderRadius: string;
  collapsed?: boolean;
  styles?: TextComponentStyles;
}>`
  align-items: flex-start;
  background: rgb(230, 230, 230);
  color: ${({ styles }) =>
    styles != null ? colorCss(styles?.['text-color'].base.base) : Color.CoreSlate800};
  padding: ${OVERLAY_HINT_VERTICAL_PADDING}px 12px;
  display: flex;
  /**
   * need to specify inner border radius to prevent overlapping into the parent border corner
   * when inside flipped mode, because we don't have overflow:hidden since it would break backface visbility for flip animation
   * border is necessary in all 4 corners on the hint when collapsed,
   * but only bottom left and bottom right corner when not collapsed
   * NOTE: Need two spaces between interpolation because Styled Components concatenates for some reason.
   */
  border-radius: ${({ innerBorderRadius }) => innerBorderRadius};
  height: 100%;
  width: 100%;
  /**
   * if transitioning to collapsed state, set border-radius to final state at end
   * when transitioning to uncollapsed state, set border-radius to final state at start
   */
  transition: ${({ collapsed }) => `height ${FLIP_ANIMATION_DURATION_SECONDS}s,
    border-radius ${COLLAPSE_ANIMATION_DURATION_SECONDS}s steps(1, ${
      collapsed ? 'jump-end' : 'jump-start'
    })`};
`;

const CaptureFrameLabelInner = styled.div`
  display: flex;
  align-items: center;

  font-size: 14px;
  line-height: 150%;

  img,
  svg {
    height: auto !important;
    width: 40px !important;
  }

  svg {
    path {
      mix-blend-mode: normal !important; // passport image has mix-blend-mode: darken which causes flip back-side to not work
    }
  }
`;

const PictographContainer = styled.span`
  flex: 1 0 auto;
  height: auto !important;
  width: 40px !important;
`;

const CaptureFrameLabelText = styled('div')`
  align-self: center;
  margin-left: 12px;
`;

const flash = keyframes`
  0% {
    opacity: 100;
  }
  100% {
    opacity: 0;
  }
`;

const scanBorderAnimation = keyframes`
  0% {
    stroke-dashoffset: 400;
  }

  100% {
    stroke-dashoffset: 0;
  }
`;

const ScanSvgWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
`;

const AnimatedBorder = styled.rect<{
  styles?: SerializedStepGovernmentIdStyles['captureFeedBox' | 'reviewImageBox'];
}>`
  // SVG draws stroke half within and half outside the bounding box of the rectangle
  // we need to draw entirely within the bounding box to match how html borders are drawn
  // so we need to inset by half the border width on each side
  width: ${({ styles }) => `calc(100% - ${getBorderWidth(styles)})`};
  height: ${({ styles }) => `calc(100% - ${getBorderWidth(styles)})`};
  x: ${({ styles }) => `calc(${getBorderWidth(styles)} / 2)`};
  y: ${({ styles }) => `calc(${getBorderWidth(styles)} / 2)`};
  path-length: 400; // this sets the units used for stroke-dasharray and stroke-dashoffset (entire perimiter is 400 units)
  fill: none;
  stroke: ${({ theme, styles }) => getScanAnimationColor(theme, styles)};
  stroke-width: ${({ styles }) => getBorderWidth(styles)};
  stroke-linecap: round;
  stroke-dasharray: 100 300;
  animation:
    ${fadeIn} 4s ease-in,
    ${scanBorderAnimation} 4s linear infinite;
`;

const Flash = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: ${COLORS.white};
  opacity: 0;
  animation: ${flash} 1s;
`;

const CapturedImage = styled('img')`
  // ensure image stays centered as div shrinks during animation
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const RealCaptureWindow = styled.div<{
  visibleToRealCaptureWindowHeightRatio: number;
  visibleToRealCaptureWindowWidthRatio: number;
}>`
  position: absolute;
  border-radius: 5px 5px 0 0;
  overflow: hidden;

  top: ${({ visibleToRealCaptureWindowHeightRatio }) =>
    (100 - 100 / visibleToRealCaptureWindowHeightRatio) / 2}%;
  left: ${({ visibleToRealCaptureWindowWidthRatio }) =>
    (100 - 100 / visibleToRealCaptureWindowWidthRatio) / 2}%;
  height: ${({ visibleToRealCaptureWindowHeightRatio }) =>
    100 / visibleToRealCaptureWindowHeightRatio}%;
  width: ${({ visibleToRealCaptureWindowWidthRatio }) =>
    100 / visibleToRealCaptureWindowWidthRatio}%;
`;

const HAS_REQUEST_IDLE_CALLBACK = window.requestIdleCallback != null;

const InnerOverlay = (props: ScannerOverlayProps & { flipped?: boolean }) => {
  const imageContainerSizerRef = useRef<HTMLDivElement>();
  // initial state of window is collapsed if props.collapsed is true or if we are supposed to display expand animation on mount
  const [collapsed, setCollapsed] = useState(props.collapsed || props.expandOnMount);
  // initial height of capture window is 0 if we are starting in a collapsed state, otherwise fit the content.
  const [height, setHeight] = useState(collapsed ? 0 : 'auto');
  const [scanVisible, setScanVisible] = useState(!collapsed);
  const [overlayIsReady, setIsOverlayReady] = useState(!collapsed);
  const [windowImageIsReady, setIsWindowImageReady] = useState(!props.captureOverlay?.['overlay']);
  const [t] = useTranslation();

  const forwardCaptureFrameLabelRef = useRef<HTMLDivElement>();
  const reverseCaptureFrameLabelRef = useRef<HTMLDivElement>();

  const [flipped, setFlipped] = useState(props.flipped);
  const [captureFrameLabelHeight, setCaptureFrameLabelHeight] = useState<number | string>('auto');

  const [collapsedVerticalOffset, setCollapsedVerticalOffset] = useState<string | null>(null);

  const [isCameraReady, setIsCameraReady] = useState(props.isCameraReady);

  const innerBorderRadius = getInnerBorderRadius(props?.styles?.captureBox);

  const [lastHint, setLastHint] = useState(props.hint);

  const aboveFrameText = props.isShortBarcodeOverlay ? props.hint || props.prompt : props.prompt;
  const inFrameText = props.isShortBarcodeOverlay ? null : props.hint;

  // Animation stroke-dashoffset consumes an unexpectedly high amount of GPU resources.
  // It behaves fine on desktop but may cause issues on mobile. For now, simply skip this.
  const showScanAnimation = false;

  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (props.hint?.length > 0) {
      setLastHint(props.hint);
    }
  }, [props.hint]);

  useEffect(() => {
    // The height is determined by imageContainerSizerRef for Dynamic Gov ID
    const captureWindowHeight = props.captureOverlay
      ? imageContainerSizerRef.current?.clientHeight
      : props.previewViewfinderRef?.current?.scrollHeight;
    if (isMounted()) {
      // if the current state of the capture window is not collapsed, but the props are trying to set it to collapsed,
      // first set the height to the computed height, then set it to 0 in the next update cycle.
      // we do this because height: auto cannot transition properly.
      if (props.collapsed && !collapsed) {
        setHeight(captureWindowHeight + 'px');
        if (HAS_REQUEST_IDLE_CALLBACK) {
          window.requestIdleCallback(
            () => {
              setHeight(0);
              // set the collapsed vertical offset to be 50% of the collapsed height manually
              // this is because the css transition doesn't work in safari since it uses
              // the initial height to calculate translateY(-50%) instead of the final height
              setCollapsedVerticalOffset(
                `${
                  -(
                    // prettier-ignore
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    (forwardCaptureFrameLabelRef.current?.scrollHeight +
                      OVERLAY_HINT_VERTICAL_PADDING * 2)
                  ) / 2
                }px`,
              );
              setCollapsed(true);
            },
            { timeout: 500 },
          );
        } else {
          setTimeout(() => {
            setHeight(0);
            setCollapsedVerticalOffset(
              `${
                -(
                  // prettier-ignore
                  /* @ts-expect-error [strict-migration] not strict; please fix! */
                  (forwardCaptureFrameLabelRef.current?.scrollHeight +
                    OVERLAY_HINT_VERTICAL_PADDING * 2)
                ) / 2
              }px`,
            );
            setCollapsed(true);
          }, 50);
        }
      } else {
        setHeight(props.collapsed ? 0 : captureWindowHeight || 'auto');

        if (props.collapsed) {
          setCollapsedVerticalOffset(
            `${
              -(
                // prettier-ignore
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                (forwardCaptureFrameLabelRef.current?.scrollHeight +
                  OVERLAY_HINT_VERTICAL_PADDING * 2)
              ) / 2
            }px`,
          );
        }

        setCollapsed(props.collapsed);
      }
    } else {
      // if this is the first render, and expandOnMount is set to true, the height is set to 0 initially.
      // Here, we set the height to the computed height of the content so it expands, and we also set the
      // state to reflect the true state of the component and set/animate its top offset.
      if (props.expandOnMount) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        setHeight(captureWindowHeight);
        setCollapsed(false);
      } else {
        setIsOverlayReady(true);
        // call onReady immeidately if we aren't animating anything fron the start, otherwise the onReady will be handled by the onTransitionEnd callback
        props.onReady?.();
      }
    }
  }, [props.collapsed]);

  useEffect(() => {
    if (isMounted()) {
      if (props.flipped && !flipped) {
        // this animates the card shrinking if the text on the reverse side is shorter than the text on the forward side
        // like with the capture window height, we have to first set the height to the computed content height plus its padding, before setting it to the
        // computed height for the reverse side
        setCaptureFrameLabelHeight(
          `${
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            forwardCaptureFrameLabelRef.current?.scrollHeight + OVERLAY_HINT_VERTICAL_PADDING * 2
          }px`,
        );
        if (HAS_REQUEST_IDLE_CALLBACK) {
          window.requestIdleCallback(
            () => {
              const newHeight =
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                reverseCaptureFrameLabelRef.current?.scrollHeight +
                OVERLAY_HINT_VERTICAL_PADDING * 2;
              setCaptureFrameLabelHeight(`${newHeight}px`);
              setCollapsedVerticalOffset(`-${newHeight / 2}px`);
              setFlipped(true);
            },
            { timeout: 500 },
          );
        } else {
          setTimeout(() => {
            const newHeight =
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              reverseCaptureFrameLabelRef.current?.scrollHeight + OVERLAY_HINT_VERTICAL_PADDING * 2;
            setCaptureFrameLabelHeight(`${newHeight}px`);
            setCollapsedVerticalOffset(`-${newHeight / 2}px`);
            setFlipped(true);
          }, 50);
        }
      }
    }
  }, [props.flipped]);

  useEffect(() => {
    if (HAS_REQUEST_IDLE_CALLBACK) {
      const callbackId = window.requestIdleCallback(
        () => {
          setIsCameraReady(props.isCameraReady);
        },
        { timeout: 500 },
      );

      return () => window.cancelIdleCallback(callbackId);
    } else {
      const callbackId = setTimeout(() => {
        setIsCameraReady(props.isCameraReady);
      }, 50);

      return () => window.clearTimeout(callbackId);
    }
  }, [props.isCameraReady]);

  // need to set mounted state here so that it takes place after the collapsed and flipped callbacks
  // so that those functions have an accurate picture of the current component state.
  const isMounted = useIsMounted();

  const renderAnimation = () => {
    if (props.capturing) {
      return <Flash />;
    }
  };

  const shouldHideGuide =
    !(windowImageIsReady && overlayIsReady) ||
    props.capturing ||
    props.hasDetectedQuad ||
    !!inFrameText;

  const isShortBarcodeOverlay = props.isShortBarcodeOverlay;

  const renderWindowImage = () => {
    if (props.governmentIdClass == null) {
      // Don't show any specific overlay for unknown IDs.
      return <SimpleGuide />;
    } else if (props.requiresSide === GovernmentIdSide.PassportSignature) {
      return <SimpleGuide />;
    } else if (['nric'].includes(props.governmentIdClass)) {
      // https://www.notion.so/withpersona/START-HERE-Malacca-Update-format-of-GovID-overlay-guidance-576ace2518f64284bf3fccf94e01b760
      return <SimpleGuide />;
    } else if (['pp', 'visa', 'ipp'].includes(props.governmentIdClass)) {
      // potentially migrate this such that pp takes MRZ_FRONT as a side instead
      return <MrzTwoLineGuide mirrored={props.mirrored} hideGuide={props.capturing} />;
    } else if (props.requiresSide === GovernmentIdSide.Front) {
      return (
        <IdFrontGuide
          mirrored={props.mirrored}
          hideGuide={shouldHideGuide}
          highlightCorners={props.hasDetectedQuad}
        />
      );
    } else if (props.requiresSide === GovernmentIdSide.Back) {
      return (
        <IdBackGuide
          mirrored={props.mirrored}
          hideGuide={shouldHideGuide}
          highlightCorners={props.hasDetectedQuad}
        />
      );
    } else if (props.requiresSide === GovernmentIdSide.BarcodePdf417) {
      if (props.isShortBarcodeOverlay) {
        return <IdBarcodeGuide mirrored={props.mirrored} hideGuide={shouldHideGuide} />;
      } else {
        // FIXME: This should never happen. isShortBarcodeOverlay is true when side is BarcodePdf417.
        // If this alert never fires, this else block can be entirely deleted.
        captureMessage(
          'Used full barcode overlay for BarcodePdf417; this should never happen.',
          'warning',
        );
        return (
          <SimpleGuide>
            {props.scanning && (
              <BarcodeImageContainer>
                <IdBarcodeGenericGuideStyled preserveAspectRatio='none' />
              </BarcodeImageContainer>
            )}
          </SimpleGuide>
        );
      }
    } else {
      return <SimpleGuide />;
    }
  };

  const renderOverlayFallback = (overlay: ClientSideOverlay) => {
    switch (overlay) {
      case ClientSideOverlay.Passport:
        return (
          <MrzTwoLineGuide
            mirrored={false} // Mirroring handled by Positioner
            hideGuide={shouldHideGuide}
          />
        );
      case ClientSideOverlay.IdFront:
        return (
          <IdFrontGuide
            mirrored={false} // Mirroring handled by Positioner
            hideGuide={shouldHideGuide}
            highlightCorners={props.hasDetectedQuad}
          />
        );
      case ClientSideOverlay.IdFrontRightPortrait:
        return (
          <IdFrontRightPortraitGuide
            mirrored={false} // Mirroring handled by Positioner
            hideGuide={shouldHideGuide}
            highlightCorners={props.hasDetectedQuad}
          />
        );
      case ClientSideOverlay.IdBack:
        return (
          <IdBackGuide
            mirrored={false} // Mirroring handled by Positioner
            hideGuide={shouldHideGuide}
            highlightCorners={props.hasDetectedQuad}
          />
        );
      case ClientSideOverlay.Barcode:
        if (props.isShortBarcodeOverlay) {
          // True if current side is pdf417
          return (
            <IdBarcodeGuide
              mirrored={false} // Mirroring handled by Positioner
              hideGuide={shouldHideGuide}
            />
          );
        } else {
          // This is a full-height capture window, because we want the user to
          // capture the full back. It is used when we are capturing back but
          // configure dynamic gov ID to show the barcode overlay. This is the
          // default config when country code is US and current side is back.
          // @see Inquiry::Step::DynamicFlow::GovernmentId::IdClasses#get_default_overlay_for_side
          return (
            <SimpleGuide>
              {props.scanning && (
                <BarcodeImageContainer>
                  <IdBarcodeGenericGuideStyled preserveAspectRatio='none' />
                </BarcodeImageContainer>
              )}
            </SimpleGuide>
          );
        }
      case ClientSideOverlay.CornersOnly:
        return <IdCornersOnlyGuide mirrored={false} highlightCorners={props.hasDetectedQuad} />;
      case ClientSideOverlay.Empty:
      default:
        return <SimpleGuide />;
    }
  };

  /**
   * Q: Why do we define these styles here?
   * A: We want to outright remove the `transform` property when it's not used. More info here:
   * https://github.com/persona-id/persona-web/issues/42420
   */
  const flipCardStyle = collapsed
    ? {
        transform: `translate3d(0, ${collapsedVerticalOffset ?? '-50%'}, 0)`,
      }
    : undefined;
  const flipCardInnerStyle = flipped
    ? {
        transform: 'rotateY(180deg)',
      }
    : undefined;

  const showPictographContainer = useMemo(() => {
    return !hidePictographContainer(props.assets, props.requiresSide, props.governmentIdClass);
  }, [props.assets, props.requiresSide, props.governmentIdClass]);

  return (
    <FlipCard
      collapsed={collapsed}
      collapsedVerticalOffset={collapsedVerticalOffset}
      isShortBarcodeOverlay={props.isShortBarcodeOverlay}
      style={flipCardStyle}
    >
      <AboveFrameHintContainer visible={!!aboveFrameText && !collapsed && overlayIsReady}>
        <div role='alert'>
          {props.videoReady
            ? aboveFrameText || (isShortBarcodeOverlay ? lastHint : null)
            : t('government-id.capture.loading-camera')}
        </div>
      </AboveFrameHintContainer>
      {showScanAnimation && isCameraReady && scanVisible && props.scanning && (
        <ScanSvgWrapper aria-hidden='true'>
          <svg role='img' width='100%' height='100%'>
            <AnimatedBorder
              height='100%'
              width='100%'
              pathLength={400}
              styles={props.styles?.captureBox}
              rx={getBorderRadius(props.styles?.captureBox)}
              ry={getBorderRadius(props.styles?.captureBox)}
            />
          </svg>
        </ScanSvgWrapper>
      )}
      <FlipCardInner flipped={flipped} styles={props.styles?.captureBox} style={flipCardInnerStyle}>
        {/* Renders both the current and reversed portraits for the flip animation */}
        {/* Current side */}
        <OverlayPortrait isReverseSide={false}>
          <ImageContainerWrapper>
            <RealCaptureWindow
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ref={props.overlayCaptureWindowRef}
              visibleToRealCaptureWindowWidthRatio={VISIBLE_TO_REAL_CAPTURE_WINDOW_WIDTH_RATIO}
              visibleToRealCaptureWindowHeightRatio={
                isShortBarcodeOverlay
                  ? SHORT_BARCODE_VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
                  : VISIBLE_TO_REAL_CAPTURE_WINDOW_HEIGHT_RATIO
              }
            >
              {props.capturedImageUrl && (
                <CapturedImage
                  alt={props.captureFrameLabel}
                  data-test='scanner-overlay__captured-image'
                  src={props.capturedImageUrl}
                />
              )}
            </RealCaptureWindow>
            <ImageContainer
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ref={props.previewViewfinderRef}
              style={{
                height: height,
              }}
              onTransitionEnd={() => {
                // set height back to auto instead of the computed height so it we resize properly when screen resizes
                setHeight(collapsed ? 0 : 'auto');
                setScanVisible(!collapsed);
                setIsOverlayReady(true);
                props.onReady?.();
              }}
            >
              {!isShortBarcodeOverlay && (
                <NewHintContainer visible={!!inFrameText && !collapsed && overlayIsReady}>
                  <NewHintContainerInner role='alert'>
                    {inFrameText || lastHint}
                  </NewHintContainerInner>
                </NewHintContainer>
              )}
              {props.captureOverlay ? (
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                <ImageContainerSizer
                  isShortBarcodeOverlay={isShortBarcodeOverlay}
                  ref={imageContainerSizerRef}
                >
                  {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                  <Positioner mirrored={props.mirrored}>
                    <CustomAssetWithFallback
                      customImage={props.captureOverlay['overlay']}
                      onLoad={() => setIsWindowImageReady(true)}
                      environment={props.environment}
                      alt='ID capture overlay'
                    >
                      {renderOverlayFallback(props.captureOverlay['overlay-fallback'])}
                    </CustomAssetWithFallback>
                  </Positioner>
                  {renderAnimation()}
                </ImageContainerSizer>
              ) : (
                <>
                  {renderWindowImage()}
                  {renderAnimation()}
                </>
              )}
            </ImageContainer>
          </ImageContainerWrapper>
          <CaptureFrameLabel
            collapsed={collapsed}
            style={{ height: captureFrameLabelHeight }}
            styles={props.styles?.hint}
            innerBorderRadius={innerBorderRadius}
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <CaptureFrameLabelInner ref={forwardCaptureFrameLabelRef}>
              {showPictographContainer && (
                <PictographContainer aria-hidden='true'>
                  <IdPictograph
                    assets={props.assets}
                    currentSide={props.requiresSide}
                    governmentIdClass={props.governmentIdClass}
                    styles={props.styles?.captureHintIcon}
                  />
                </PictographContainer>
              )}
              <CaptureFrameLabelText>{props.captureFrameLabel}</CaptureFrameLabelText>
            </CaptureFrameLabelInner>
          </CaptureFrameLabel>
        </OverlayPortrait>

        {/* Reverse side */}
        <OverlayPortrait isReverseSide={true} aria-hidden='true'>
          <CaptureFrameLabel
            collapsed={collapsed}
            style={{ height: captureFrameLabelHeight }}
            styles={props.styles?.hint}
            innerBorderRadius={innerBorderRadius}
          >
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            <CaptureFrameLabelInner ref={reverseCaptureFrameLabelRef}>
              {showPictographContainer && (
                <PictographContainer aria-hidden='true'>
                  <IdPictograph
                    assets={props.assets}
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    currentSide={props.reverseSide}
                    governmentIdClass={props.governmentIdClass}
                    styles={props.styles?.captureHintIcon}
                  />
                </PictographContainer>
              )}
              <CaptureFrameLabelText>{props.reverseCaptureFrameLabel}</CaptureFrameLabelText>
            </CaptureFrameLabelInner>
          </CaptureFrameLabel>
        </OverlayPortrait>
      </FlipCardInner>
      {props.renderUnderOverlayComponents && (
        <UnderOverlayComponentsWrapper visible={!collapsed}>
          {props.renderUnderOverlayComponents()}
        </UnderOverlayComponentsWrapper>
      )}
    </FlipCard>
  );
};

export default ScannerOverlay;

const IdPictograph = ({
  assets,
  currentSide,
  governmentIdClass,
  styles,
}: {
  assets: StepGovernmentIdConfig['assets']['capture-page'];
  currentSide: GovernmentIdSide;
  governmentIdClass: GovernmentIdClass;
  styles: SerializedStepGovernmentIdStyles['captureHintIcon'];
}) => {
  if (governmentIdClass === 'pp' || governmentIdClass === 'ipp') {
    switch (currentSide) {
      case GovernmentIdSide.PassportSignature:
        return (
          <CustomAssetWithFallback
            customImage={assets['passport-signature-pictograph']}
            alt='passport signature pictograph'
          >
            <ThemedSvg
              style={{ lineHeight: 0 }}
              $primaryColor={colorCss(styles?.fill?.color)}
              $strokeColor={colorCss(styles?.stroke?.color)}
              aria-hidden='true'
            >
              <ImagePassportSignature />
            </ThemedSvg>
          </CustomAssetWithFallback>
        );
      default:
        return (
          <CustomAssetWithFallback
            customImage={assets['passport-front-pictograph']}
            alt='passport front pictograph'
          >
            <ThemedSvg
              style={{ lineHeight: 0 }}
              $primaryColor={colorCss(styles?.fill?.color)}
              $strokeColor={colorCss(styles?.stroke?.color)}
              aria-hidden='true'
            >
              <ImagePassportPortrait />
            </ThemedSvg>
          </CustomAssetWithFallback>
        );
    }
  }

  switch (currentSide) {
    case GovernmentIdSide.Back:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-back-pictograph']}
          alt='ID back pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImageIdBack style={{ height: '46px', width: '74px' }} />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.BarcodePdf417:
      return (
        <CustomAssetWithFallback
          customImage={assets['barcode-pdf417-pictograph']}
          alt='Barcode PDF417 pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImageIdBarcode style={{ height: '46px', width: '74px' }} />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.Front:
    case GovernmentIdSide.FrontOrBack:
      return (
        <CustomAssetWithFallback
          customImage={assets['id-front-pictograph']}
          alt='ID front pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImageIdFront style={{ height: '46px', width: '74px' }} />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    case GovernmentIdSide.PassportSignature:
      return (
        <CustomAssetWithFallback
          customImage={assets['passport-signature-pictograph']}
          alt='passport signature pictograph'
        >
          <ThemedSvg
            style={{ lineHeight: 0 }}
            $primaryColor={colorCss(styles?.fill.color)}
            $strokeColor={colorCss(styles?.stroke.color)}
            aria-hidden='true'
          >
            <ImagePassportSignature />
          </ThemedSvg>
        </CustomAssetWithFallback>
      );

    default:
      return null;
  }
};

// NOTE: since the width of PictographContainer is hardcoded with !important,
// there is a large whitespace when the asset is meant to be hidden
// TODO: replace bandaid with better handling of custom sizes in PER-27209
const hidePictographContainer = (
  assets: StepGovernmentIdConfig['assets']['capture-page'],
  currentSide: GovernmentIdSide,
  governmentIdClass: GovernmentIdClass,
) => {
  let currentAsset;

  if (governmentIdClass === 'pp' || governmentIdClass === 'ipp') {
    if (currentSide === GovernmentIdSide.PassportSignature) {
      currentAsset = assets['passport-signature-pictograph'];
    } else {
      currentAsset = assets['passport-front-pictograph'];
    }
  } else {
    if (currentSide === GovernmentIdSide.Back) {
      currentAsset = assets['id-back-pictograph'];
    } else if (currentSide === GovernmentIdSide.BarcodePdf417) {
      currentAsset = assets['barcode-pdf417-pictograph'];
    } else if (
      currentSide === GovernmentIdSide.Front ||
      currentSide === GovernmentIdSide.FrontOrBack
    ) {
      currentAsset = assets['id-front-pictograph'];
    } else if (currentSide === GovernmentIdSide.PassportSignature) {
      currentAsset = assets['passport-signature-pictograph'];
    }
  }

  return (
    currentAsset?.styles?.width?.base?.base?.value === 0 ||
    currentAsset?.attributes?.width === '0px'
  );
};
